import { Button, Col, Form, Row } from "antd";
import TextArea from "antd/lib/input/TextArea";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { RouteChildrenProps, useHistory } from "react-router";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import {
  FormChangeDebounceTime,
  VALIDATION_MESSAGE_CONFIG,
} from "../../../../../../constants/constants";
import { ROUTE_PATHS } from "../../../../../../constants/url-config";
import { AppraisalModel } from "../../../../../../models/appraisal.model";
import { MessageType } from "../../../../../../models/common/response-message";
import {
  FormBlockModel,
  FormSectionModel,
  FormSectionProps,
} from "../../../../../../models/form-section.model";
import { ResourcesModel } from "../../../../../../models/resources.model";
import { setActions } from "../../../../../../redux/actions/header.action";
import { ClientService } from "../../../../../../services/client.service";
import { ERMService } from "../../../../../../services/erm.service";
import i18n from "../../../../../../utils/i18n";
import UTIL from "../../../../../../utils/utils";
import { VALIDATION_UTIL } from "../../../../../../utils/validation.utils";
import AppAnchor from "../../../../../atoms/anchor";
import { showMessage } from "../../../../../atoms/notification/notification";
import "./appraisal-form.scss";
import BreackDownSection from "./breack-down-section";
import Calculations from "./calculations";
import PurposeFulfillment from "./purpose-fulfillment-section";

const APPRAISAL_SECTION = [
  {
    title: i18n.t("Appraisal.lbPurposeFulfillment"),
    id: 2,
    is_completed: false,
    sectionComponent: PurposeFulfillment,
  },
  {
    title: i18n.t("Appraisal.lbBreakdownFunding"),
    id: 3,
    is_completed: false,
    sectionComponent: BreackDownSection,
  },
  {
    title: i18n.t("Appraisal.lbROICalculations"),
    id: 4,
    is_completed: false,
    sectionComponent: Calculations,
  },
];

const formChanged = new Subject<AppraisalModel>();

interface BreackDownBenefits extends FormBlockModel {
  details: FormBlockModel;
}

export interface AppraisalSectionProps
  extends FormSectionProps<AppraisalModel> {
  purpose_fulfillment?: FormBlockModel;
  breakdown_of_funding_and_benefits?: BreackDownBenefits;
}

interface Props extends RouteChildrenProps<{ scenarioId: string }> {}

const AppraisalForm = (props: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const scenatioId = React.useRef(0);
  const [data, setData] = useState<AppraisalModel>({});
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [resources, setResources] = useState<ResourcesModel>({});
  const [formValidateTrigger, setFormValidateTrigger] = useState(["onFinish"]);
  const valuesChanged = useRef<AppraisalModel>({});
  const [purposeFulfillment, setPurposeFulfillment] = useState<
    FormBlockModel | undefined
  >();
  const [breakdownAndBenefits, setBreakdownAndBenefits] = useState<
    BreackDownBenefits | undefined
  >();
  const history = useHistory();
  const dispatch = useDispatch();

  const sections = useRef<FormSectionModel[]>(APPRAISAL_SECTION);

  const next = () => {
    setFormValidateTrigger(["onFinish", "onChange"]);
    form.submit();
  };

  const autoSave = () => {
    if (valuesChanged.current.breakdown_of_funding_and_benefits) {
      valuesChanged.current.breakdown_of_funding_and_benefits = form.getFieldValue(
        "breakdown_of_funding_and_benefits"
      );
    }

    if (valuesChanged.current.purpose_fulfillment) {
      valuesChanged.current.purpose_fulfillment = form.getFieldValue(
        "purpose_fulfillment"
      );
    }

    const value = UTIL.prepareAppraisalFormForSubmit(valuesChanged.current);
    valuesChanged.current = {};
    ERMService.saveDraftAppraisalAsync(scenatioId.current, value, false).then(
      (res) => {
        UTIL.setSectionIsCompleted(
          res.data._section_statuses,
          sections.current
        );
        setData(res.data);
      }
    );
  };

  const formChange = (
    changedValues: AppraisalModel,
    values: AppraisalModel
  ) => {
    valuesChanged.current = { ...valuesChanged.current, ...changedValues };
    formChanged.next(valuesChanged.current);
  };

  const formSubmit = (value: AppraisalModel) => {
    value = UTIL.prepareAppraisalFormForSubmit(value);
    ERMService.updateAppraisalAsync(scenatioId.current, value).then((res) => {
      showMessage({
        message: t("Global.lbSavedSuccessfully"),
        type: MessageType.success,
      });
      history.push(ROUTE_PATHS.Scenarios);
    });
  };

  const goback = () => {
    props.history.goBack();
  };

  const renderActions = () => (
    <>
      <Button
        onClick={goback}
        htmlType="button"
        type="default"
        shape="round"
        block
      >
        {t("Appraisal.btnGoback")}
      </Button>
      <Button
        onClick={next}
        htmlType="button"
        type="primary"
        shape="round"
        block
      >
        {t("Appraisal.btnSubmit")}
      </Button>
    </>
  );

  useEffect(() => {
    const formChangedSub = formChanged
      .pipe(debounceTime(FormChangeDebounceTime))
      .subscribe(() => {
        autoSave();
      });

    const id = Number(props.match?.params.scenarioId);
    scenatioId.current = id;

    const actions = renderActions();
    dispatch(setActions(actions));

    ERMService.newAppraisalAsync(id).then((res) => {
      if (res.data) {
        const newData: AppraisalModel = UTIL.convertAppraisalForm(res.data);
        UTIL.setSectionIsCompleted(newData._section_statuses, sections.current);
        setData(newData);
        form.setFieldsValue(newData);
        setRequiredFields(UTIL.getAllRequiredFields(newData._sections));

        const purpose_fulfillment = newData._sections?.find(
          (x) => x.field_conditions && x.field_conditions.purpose_fulfillment
        );
        if (purpose_fulfillment) {
          setPurposeFulfillment(
            purpose_fulfillment.field_conditions.purpose_fulfillment
          );
        }

        const breakdown_of_funding_and_benefitsSS = newData._sections?.find(
          (x) =>
            x.field_conditions &&
            x.field_conditions.breakdown_of_funding_and_benefits
        );
        if (breakdown_of_funding_and_benefitsSS) {
          const breakdown_of_funding_and_benefits =
            breakdown_of_funding_and_benefitsSS.field_conditions
              .breakdown_of_funding_and_benefits;
          setBreakdownAndBenefits({
            min_element: breakdown_of_funding_and_benefits.min_element,
            required_fields: breakdown_of_funding_and_benefits.required_fields,
            details:
              breakdown_of_funding_and_benefits.field_conditions?.details,
          });
        }
      }
    });

    ClientService.getResourcesAsync().then((res) => {
      setResources(res.data);
    });

    return () => {
      dispatch(setActions(undefined));
      formChangedSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="appraisal-form-wrapper">
      <Row gutter={20}>
        <Col xs={0} sm={5}>
          <AppAnchor sections={sections.current} />
        </Col>
        <Col xs={0} sm={14}>
          <Form
            className="main-form"
            layout="vertical"
            form={form}
            validateMessages={VALIDATION_MESSAGE_CONFIG}
            onValuesChange={(
              changedValues: AppraisalModel,
              values: AppraisalModel
            ) => {
              formChange(changedValues, values);
            }}
            validateTrigger={formValidateTrigger}
            onFinish={(values) => formSubmit(values)}
            scrollToFirstError
          >
            <div className="form-section">
              <div className="section-wrapper">
                <Row gutter={[16, 32]}>
                  <Col span={24}>
                    <Form.Item
                      label={t("Appraisal.lbProjectDescription")}
                      name="project_description"
                      rules={VALIDATION_UTIL.getRulesValidationField(
                        "project_description",
                        requiredFields
                      )}
                    >
                      <TextArea
                        rows={5}
                        placeholder={t("Appraisal.plProjectDescription")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>

            {sections.current.map((item, index) => (
              <div
                id={`section${item.id}`}
                key={index}
                className="form-section"
              >
                <p className="section-title">{item.title}</p>
                <item.sectionComponent
                  data={data}
                  requiredFields={requiredFields}
                  resources={resources}
                  purpose_fulfillment={purposeFulfillment}
                  breakdown_of_funding_and_benefits={breakdownAndBenefits}
                />
              </div>
            ))}

            <Row gutter={12}>
              <Col span={24} className="form-action">
                {renderActions()}
              </Col>
            </Row>
          </Form>
        </Col>
        <Col xs={0} sm={5}></Col>
      </Row>
    </div>
  );
};

export default AppraisalForm;
